import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
  type UseQueryOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import { dailyRevealApi } from '@/api/dailyRevealApi';
import type {
  DaySummary,
  RevealPackResponse,
  GetGameResponse,
  DayFixture,
  GetUserEntryResponse,
  GetContestRulesResponse,
} from '@/types/reveal';
import { toTeamAbbreviation } from '@/utils/teamAbbreviation';

async function getUserEntryOrNull(dayId: string): Promise<GetUserEntryResponse | null> {
  try {
    return await dailyRevealApi.getUserEntry(dayId);
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export const dailyRevealKeys = {
  games: ['daily-reveal', 'games'] as const,
  game: (gameId: string) => ['daily-reveal', 'game', gameId] as const,
  rules: (gameId: string) => ['daily-reveal', 'rules', gameId] as const,
  entry: (dayId: string) => ['daily-reveal', 'entry', dayId] as const,
  fixtures: (dayId: string) => ['daily-reveal', 'fixtures', dayId] as const,
};

export function useListGames() {
  return useQuery({
    queryKey: dailyRevealKeys.games,
    queryFn: () => dailyRevealApi.listGames(),
    staleTime: 60_000,
  });
}

/** Game details, including its days and the user's entry status. */
export function useGameDetails(gameId: string | null, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: dailyRevealKeys.game(gameId ?? ''),
    queryFn: () => dailyRevealApi.getGame(gameId!),
    enabled: !!gameId,
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Details for several games at once; feeds the contest week selector. */
export function useGamesDetails(gameIds: string[], options?: { enabled?: boolean }) {
  return useQueries({
    queries: gameIds.map(gameId => ({
      queryKey: dailyRevealKeys.game(gameId),
      queryFn: () => dailyRevealApi.getGame(gameId),
      enabled: (options?.enabled ?? true) && !!gameId,
      staleTime: 5 * 60_000,
    })),
  });
}

export function useContestRules(gameId: string | null) {
  return useQuery<GetContestRulesResponse>({
    queryKey: dailyRevealKeys.rules(gameId ?? ''),
    queryFn: () => dailyRevealApi.getContestRules(gameId!),
    enabled: !!gameId,
    staleTime: 5 * 60_000,
  });
}

export function useUserEntry(
  dayId: string | null,
  options?: Pick<
    UseQueryOptions<GetUserEntryResponse | null, Error, GetUserEntryResponse | null>,
    'refetchInterval'
  >
) {
  return useQuery({
    queryKey: dailyRevealKeys.entry(dayId ?? ''),
    queryFn: () => getUserEntryOrNull(dayId!),
    enabled: !!dayId,
    staleTime: 10_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useDayFixtures(dayId: string | null, options?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: dailyRevealKeys.fixtures(dayId ?? ''),
    queryFn: () => dailyRevealApi.getDayFixtures(dayId!),
    enabled: !!dayId,
    staleTime: 15_000,
    refetchInterval: options?.refetchInterval,
  });
}

export function useRevealPack() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dayId: string) => dailyRevealApi.revealPack(dayId),
    onSuccess: (_data: RevealPackResponse, dayId: string) => {
      queryClient.invalidateQueries({ queryKey: dailyRevealKeys.entry(dayId) });
      queryClient.invalidateQueries({ queryKey: dailyRevealKeys.games });
      queryClient.invalidateQueries({ queryKey: ['daily-reveal', 'game'] });
    },
  });
}

export function usePickCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dayId, cardId }: { dayId: string; cardId: string }) =>
      dailyRevealApi.pickCard(dayId, cardId),
    onSuccess: (_data, { dayId }) => {
      queryClient.invalidateQueries({ queryKey: dailyRevealKeys.entry(dayId) });
      queryClient.invalidateQueries({ queryKey: dailyRevealKeys.games });
      queryClient.invalidateQueries({ queryKey: ['daily-reveal', 'game'] });
    },
  });
}

/**
 * Whether a Daily Reveal contest is currently playable based on its own status.
 * Used to decide history (read-only) vs play mode per-contest so a lobby/URL-opened
 * contest plays even when it isn't the single global "active" pick (DKPLAT-476).
 *
 * Mirrors the lobby's PLAYABLE_STATUS_KEYS (apps/lobby lobbyPlayableStatus.ts):
 * active/inplay/pending/open/scheduled are playable; terminal states
 * (complete/cancelled/draft/…) are history. Status strings vary in case and
 * separators ('IN_PLAY', 'in-play'), so normalise before comparing.
 */
const PLAYABLE_DAILY_REVEAL_STATUSES = new Set([
  'active',
  'inplay',
  'pending',
  'open',
  'scheduled',
]);

export function isLiveDailyRevealStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const normalized = status
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  return PLAYABLE_DAILY_REVEAL_STATUSES.has(normalized);
}

export function findActiveGame(data: {
  games: { id: string; gameType: string; sport: string; pickemStatus: string | null }[];
}): string | null {
  const mlb = data.games.filter(g => g.gameType === 'daily_reveal');
  const open = mlb.find(g => g.pickemStatus?.toUpperCase() === 'OPEN');
  if (open) return open.id;
  const inplay = mlb.find(g => g.pickemStatus?.toUpperCase() === 'INPLAY');
  if (inplay) return inplay.id;
  return mlb[0]?.id ?? null;
}

/** Derive the active day from the game response for the current date */
export function resolveCurrentDay(gameResp: GetGameResponse): DaySummary | null {
  const openDay = gameResp.days.find(d => d.status === 'open');
  if (openDay) return openDay;

  const lockedDay = gameResp.days.find(d => d.status === 'locked');
  if (lockedDay) return lockedDay;

  const today = new Date().toISOString().slice(0, 10);
  const todayDay = gameResp.days.find(d => d.gameDate === today);
  if (todayDay) return todayDay;

  const graded = [...gameResp.days].reverse().find(d => d.status === 'graded');
  return graded ?? gameResp.days[0] ?? null;
}

/**
 * How many of the week's selections were correct — the value the prize ladder is read against.
 *
 * One selection per reveal day (enforced by `unique(day_id, user_id)` on
 * `daily_reveal_entries`), so iterating days counts selections. It is keyed on the reveal DAY,
 * never on a calendar date: selections stack toward a later fixture — reveals on Tue, Wed and
 * Thu can all be picks for the Thursday game — and all three must count separately once that
 * game settles.
 *
 * A revealed-but-wrong selection counts 0; only a graded entry meeting the day's hit threshold
 * scores. Mirrors `daysWithHits` in `PrizePayoutService.calculatePrizesForDailyReveal`, which
 * is the authority at payout time.
 */
export function computeCorrectSelections(days: DaySummary[], threshold = 1): number {
  return days.reduce((count, day) => {
    if (!day.userEntry) return count;
    return count + ((day.userEntry.totalHits ?? 0) >= threshold ? 1 : 0);
  }, 0);
}

/**
 * Find the fixture matching a card's team from the fixtures list.
 *
 * Prefers Sportradar/team external IDs. Falls back to abbreviation matching so
 * odds/prop-based cards (which often have `teamExternalId: null`) still resolve
 * both teams and the game score in Weekly Selections / Today's Pick.
 */
export function findFixtureForCard(
  teamExternalId: string | null | undefined,
  fixtures: DayFixture[],
  teamAbbreviation?: string | null
): DayFixture | null {
  if (fixtures.length === 0) return null;

  if (teamExternalId) {
    const byId = fixtures.find(
      f => f.homeTeamId === teamExternalId || f.awayTeamId === teamExternalId
    );
    if (byId) return byId;
  }

  const abbr = toTeamAbbreviation(teamAbbreviation ?? '');
  if (!abbr) return null;

  return (
    fixtures.find(f => {
      const home = toTeamAbbreviation(f.homeTeam ?? '');
      const away = toTeamAbbreviation(f.awayTeam ?? '');
      return home === abbr || away === abbr;
    }) ?? null
  );
}

/**
 * Format the contest date range from game days (first to last day).
 * Returns e.g. "Mar 11 - Mar 17, 2026" or null if no days.
 */
export function formatContestDateRange(days: DaySummary[]): string | null {
  if (!days || days.length === 0) return null;

  const sorted = [...days].sort((a, b) => a.dayNumber - b.dayNumber);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first?.gameDate || !last?.gameDate) return null;

  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = new Date(first.gameDate + 'T00:00:00').toLocaleDateString('en-US', opts);
  const endStr = new Date(last.gameDate + 'T00:00:00').toLocaleDateString('en-US', {
    ...opts,
    year: 'numeric',
  });
  return `${startStr} - ${endStr}`;
}

/** Normalize sport codes for comparison (`mlb` → `MLB`). */
export function normalizeSport(sport: string | null | undefined): string | null {
  const trimmed = sport?.trim();
  if (!trimmed) return null;
  return trimmed.toUpperCase();
}

export function sportsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = normalizeSport(a);
  const right = normalizeSport(b);
  if (!left || !right) return false;
  return left === right;
}

/** Minimal game shape needed to build ContestSelector pills. */
export type ContestSelectorSourceGame = {
  game: { id: string; sport: string };
  days: Array<{ gameDate: string; userEntry?: unknown | null }>;
};

export type ContestSelectorItem = { id: string; label: string };

/**
 * Build date-range pills for week history within a single sport.
 *
 * Concurrent contests in other sports (e.g. NFL while viewing MLB) are excluded
 * so each lobby deep-link stays on its own contest page family.
 *
 * `anchorGameId` — the contest the URL currently opens — is always included, even
 * with no entry in it and even when it isn't the live week (DKPLAT-763). Without
 * that, deep-linking to a contest the player hasn't entered produced a pill row
 * where nothing matched the open contest, so the row read as "first week selected"
 * while the page below showed a different one.
 */
export function buildContestSelectorItems(options: {
  games: ContestSelectorSourceGame[];
  anchorSport: string | null | undefined;
  liveGameId: string | null | undefined;
  anchorGameId?: string | null;
  formatDateRange?: (days: ContestSelectorSourceGame['days']) => string | null;
}): ContestSelectorItem[] {
  const anchor = normalizeSport(options.anchorSport);
  if (!anchor) return [];

  const sameSport = options.games.filter(g => sportsMatch(g.game.sport, anchor));

  const included = sameSport.filter(g => g.days?.some(d => d.userEntry != null));

  const ensureIncluded = (gameId: string | null | undefined) => {
    if (!gameId || included.some(g => g.game.id === gameId)) return;
    const game = sameSport.find(g => g.game.id === gameId);
    if (game) included.unshift(game);
  };

  ensureIncluded(options.liveGameId);
  ensureIncluded(options.anchorGameId);

  const sorted = [...included].sort((a, b) => {
    const aLast = a.days.reduce((max, d) => (d.gameDate > max ? d.gameDate : max), '');
    const bLast = b.days.reduce((max, d) => (d.gameDate > max ? d.gameDate : max), '');
    return bLast.localeCompare(aLast);
  });

  const formatDateRange = options.formatDateRange ?? (() => null);
  return sorted.map(g => ({
    id: g.game.id,
    label: formatDateRange(g.days) ?? 'Contest',
  }));
}

/**
 * Get the next reveal open time (ISO string) from the game's days.
 * Used when the current day is locked/graded to show countdown to next reveal.
 */
export function getNextRevealOpenAt(
  days: DaySummary[],
  currentDay: DaySummary | null
): string | null {
  if (!currentDay || days.length === 0) return null;

  const nextDay = days
    .filter(d => d.dayNumber > currentDay.dayNumber)
    .sort((a, b) => a.dayNumber - b.dayNumber)[0];

  return nextDay?.revealOpenAt ?? null;
}

/**
 * Get the next reveal open time (ISO string) strictly after "now".
 * Unlike `getNextRevealOpenAt`, this can return a later reveal on the current day
 * (useful when `pickCutoffAt` passes before `revealOpenAt`).
 */
export function getNextRevealOpenAtForNow(days: DaySummary[], nowMs: number): string | null {
  const candidates = days
    .filter(d => !!d.revealOpenAt)
    .map(d => {
      const t = new Date(d.revealOpenAt as string).getTime();
      return { day: d, t };
    })
    .filter(x => Number.isFinite(x.t) && x.t > nowMs)
    .sort((a, b) => a.t - b.t);

  return candidates[0]?.day.revealOpenAt ?? null;
}

/**
 * Format a fixture's start time as "H:MMam/pm" (e.g. "7:05pm").
 * Returns null when the fixture has no start time.
 */
export function formatFixtureTime(fixture: DayFixture): string | null {
  if (!fixture.startTime) return null;
  return new Date(fixture.startTime)
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
    .replace(/\s?(AM|PM)$/, (_, m) => m.toLowerCase());
}

/**
 * Format a fixture's game status for display.
 */
export function formatFixtureStatus(fixture: DayFixture): string {
  switch (fixture.status) {
    case 'live':
      return fixture.clock || fixture.inning || 'Live';
    case 'finished':
      return 'Final';
    case 'postponed':
      return 'Postponed';
    case 'cancelled':
      return 'Canceled';
    case 'scheduled':
    default: {
      if (!fixture.startTime) return 'TBD';
      return new Date(fixture.startTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }
}
